import { featureFlags } from '@/lib/env';

/**
 * FE が扱うプロダクト権限スコープの正一覧。
 * 型（ProductPermissionScopeCode）はこの const から導出する。
 * セッションの Zod フィルタ（lib/auth/zod-schema.ts）やゲート判定はこの const を
 * 基準にするため、FE が「扱える」スコープの source of truth を const に一本化する。
 * バックエンドがスコープを追加したら、ここに追記すること。
 */
export const PRODUCT_PERMISSION_SCOPE_CODES = {
  // Marketplace
  MARKETPLACE: 'marketplace',
  // HR Force
  PEOPLE: 'people',
  ORGANIZATION: 'organization',
  ORGANIZATION_PLAN: 'organization-restructuring',
  PUBLIC_IDS: 'public-ids',
  CERTIFICATION: 'certification',
  LABOR_CORE: 'labor-core',
  CONTRACTOR_CORE: 'contractor-core',
  TIME_AND_ATTENDANCE: 'time-and-attendance',
  // IT Force
  // TODO: AppsからSoftwareに移行した際にUIは移行済だが、パーミッションの移行が現状できていない状態
  APPS: 'apps',
  // SOFTWARE: "software",
  // Software SSO（Outbound SSO クライアントの発行・更新・削除・有効/無効の変更系操作の権限）。
  // 取得（設定の閲覧・scope 選択肢）は引き続き apps。
  APP_SSO: 'app-sso',
  ACCESSES: 'accesses',
  DEVICES: 'devices',
  ID_MANAGEMENT: 'id-management',
  LICENSES: 'licenses',
  STORAGE_FILES: 'storage-files',
  NETWORKS: 'networks',
  STARLINK: 'starlink',
  IT_OPERATION_CORE: 'it-operation-core',
  // GA Force
  PLACE: 'place',
  EQUIPMENT: 'equipment',
  GA_OPERATION_CORE: 'ga-operation-core',
  FLEET: 'fleet',
  // LI Force
  CONTRACT: 'contract',
  BUSINESS_PARTNER: 'business-partner',
  // PRC Force
  SUPPLIER_LEDGER: 'supplier-ledger',
  DEAL_LEDGER: 'deal-ledger',
  ITEM_LEDGER: 'item-ledger',
  PO_LEDGER: 'po-ledger',
  // PN Force (STACKTRACE)
  STACKTRACE_PN: 'stacktrace-pn',
  // QA Force (STACKTRACE)
  STACKTRACE_QA: 'stacktrace-qa',
  // PP Force (STACKTRACE / プロダクト計画コア)
  STACKTRACE_PP: 'stacktrace-pp',
  // SA Force
  CUSTOMER_LEDGER: 'customer-ledger',
  // Settings
  USER_ACCOUNTS: 'user-accounts',
  PERMISSION_AND_ROLE: 'permission-and-role',
  CONTRACTING_CORPORATION_PROFILE: 'contracting-corporation-profile',
  DATA_CONNECTOR: 'data-connector',
  /** Notification And Response */
  NOTIFICATION_AND_RESPONSE: 'notification-and-response',
  // BOE
  OPERATION_EVENT: 'operation-event'
} as const;
export type ProductPermissionScopeCode = (typeof PRODUCT_PERMISSION_SCOPE_CODES)[keyof typeof PRODUCT_PERMISSION_SCOPE_CODES];
export const isProductPermissionScopeCode = (code: string): code is ProductPermissionScopeCode => {
  // NOTE: 型が追加された際もコンパイルで落ちないようにダウンキャストする
  const codes: string[] = Object.values(PRODUCT_PERMISSION_SCOPE_CODES);
  return Object.values(codes).includes(code as ProductPermissionScopeCode);
};

/**
 * 表示しないスコープのリスト
 * 画面が提供されておらず、画面に見えると誤解を招くため除外するスコープを定義
 *
 * NOTE: STACKTRACE 系スコープ（PN/QA/PP）はここでの無条件非表示から
 * FEATURE_FLAG_CONTROLLED_SCOPES（ENABLE_STACKTRACE_INCLUDE）へ移行済み。
 */
const HIDDEN_PRODUCT_PERMISSION_SCOPE_CODES: readonly ProductPermissionScopeCode[] = [] as const;

/**
 * フィーチャーフラグで制御されるスコープのマップ
 * key: スコープコード, value: フィーチャーフラグ名
 *
 * 新しいスコープをフィーチャーフラグで制御する場合は、このマップにエントリを追加してください
 */
const FEATURE_FLAG_CONTROLLED_SCOPES: Partial<Record<ProductPermissionScopeCode, Parameters<typeof featureFlags.check>[0]>> = {
  // 例: [PRODUCT_PERMISSION_SCOPE_CODES.STARLINK]: "STARLINK",
  // PRC Force / 取引台帳は DEAL_LEDGER フラグで制御（本番は flag=false で非表示）
  [PRODUCT_PERMISSION_SCOPE_CODES.DEAL_LEDGER]: 'DEAL_LEDGER',
  // PRC Force / 仕入先台帳は SUPPLIER_LEDGER フラグで制御（本番は flag=false で非表示）
  [PRODUCT_PERMISSION_SCOPE_CODES.SUPPLIER_LEDGER]: 'SUPPLIER_LEDGER',
  // PRC Force / 品目台帳は ITEM_LEDGER フラグで制御（本番は flag=false で非表示）
  [PRODUCT_PERMISSION_SCOPE_CODES.ITEM_LEDGER]: 'ITEM_LEDGER',
  // PRC Force / PO台帳は PO_LEDGER フラグで制御（本番は flag=false で非表示）
  [PRODUCT_PERMISSION_SCOPE_CODES.PO_LEDGER]: 'PO_LEDGER',
  // SA Force / 顧客台帳は CUSTOMER_LEDGER フラグで制御（本番は flag=false で非表示）
  [PRODUCT_PERMISSION_SCOPE_CODES.CUSTOMER_LEDGER]: 'CUSTOMER_LEDGER',
  // STACKTRACE / PN オペレーションコアは ENABLE_STACKTRACE_INCLUDE フラグで制御
  // （flag=true のときだけ権限設定画面に表示・編集可能）
  [PRODUCT_PERMISSION_SCOPE_CODES.STACKTRACE_PN]: 'ENABLE_STACKTRACE_INCLUDE',
  // STACKTRACE / QA オペレーションコアも ENABLE_STACKTRACE_INCLUDE フラグで制御
  [PRODUCT_PERMISSION_SCOPE_CODES.STACKTRACE_QA]: 'ENABLE_STACKTRACE_INCLUDE',
  // STACKTRACE / プロダクト計画コアも ENABLE_STACKTRACE_INCLUDE フラグで制御
  [PRODUCT_PERMISSION_SCOPE_CODES.STACKTRACE_PP]: 'ENABLE_STACKTRACE_INCLUDE',
  // IT Force / Storage & Files は STORAGE_FILES フラグで制御（本番は flag=false で非表示）
  [PRODUCT_PERMISSION_SCOPE_CODES.STORAGE_FILES]: 'STORAGE_FILES'
};

/**
 * スコープが表示対象かどうかを判定する
 * @param scopeCode スコープコード
 * @returns 表示対象の場合 true
 */
export const isVisibleProductPermissionScope = (scopeCode: string): boolean => {
  // HIDDEN_PRODUCT_PERMISSION_SCOPE_CODES に含まれている場合は非表示
  if ((HIDDEN_PRODUCT_PERMISSION_SCOPE_CODES as readonly string[]).includes(scopeCode)) {
    return false;
  }

  // フィーチャーフラグによる制御
  const featureFlag = FEATURE_FLAG_CONTROLLED_SCOPES[scopeCode as ProductPermissionScopeCode];
  if (featureFlag) {
    return featureFlags.check(featureFlag);
  }
  return true;
};

/**
 * カテゴリ内のスコープを表示対象のみにフィルタリングする
 * @param category スコープを持つカテゴリオブジェクト
 * @returns フィルタリングされたカテゴリ
 */
export const filterVisibleScopes = <T extends {
  scopes: {
    code: string;
  }[];
},>(category: T): T => ({
  ...category,
  scopes: category.scopes.filter(s => isVisibleProductPermissionScope(s.code))
});